import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Room, RoomEvent } from 'livekit-client';
import api from '../api';
import { Phone, PhoneOff, Loader2 } from 'lucide-react';

const EmbedAgentPageContent = ({ agentId }) => {
  const [agent, setAgent] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [captions, setCaptions] = useState([]);
  const [room, setRoom] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'full';
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('view');
    if (mode === 'buttons' || mode === 'minimal') return 'buttons';
    if (mode === 'dynamic') return 'dynamic';
    return 'full';
  });
  const [embedToken, setEmbedToken] = useState(null);
  const [dynamicVariableNames, setDynamicVariableNames] = useState([]); // Variable names from URL
  const [dynamicVariableValues, setDynamicVariableValues] = useState({}); // User-filled values
  const [dynamicVariableLabels, setDynamicVariableLabels] = useState({}); // User-friendly labels
  
  const audioRef = useRef(null);

  useEffect(() => {
    // Component mounted
  }, [agentId]);

  // Detect view mode from query string and extract dynamic variable names
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('view');
    if (mode === 'buttons' || mode === 'minimal') {
      setViewMode('buttons');
    } else if (mode === 'dynamic') {
      setViewMode('dynamic');
      
      // Extract variable names from URL (all params except system params)
      // Values can be empty (for user input) or pre-filled
      const systemParams = ['view', 'token', 'embed_token', 'key'];
      const variableNames = [];
      const initialValues = {};
      
      params.forEach((value, key) => {
        if (!systemParams.includes(key)) {
          variableNames.push(key);
          // If value is provided and not a template placeholder, use it as initial value
          if (value && !value.startsWith('{{') && !value.endsWith('}}')) {
            initialValues[key] = value;
          }
        }
      });
      
      setDynamicVariableNames(variableNames);
      setDynamicVariableValues(initialValues);
      console.log('📊 Dynamic variables detected:', variableNames);
      console.log('📊 Initial values:', initialValues);
    } else {
      setViewMode('full');
    }

    const tokenParam = params.get('token') || params.get('embed_token') || params.get('key');
    if (tokenParam) {
      setEmbedToken(tokenParam);
    }
  }, []);

  const minimalMode = viewMode === 'buttons';
  const buttonsOnlyMode = minimalMode && viewMode !== 'dynamic';

  // Transparent background for buttons-only embed so it blends with client sites
  useEffect(() => {
    if (!buttonsOnlyMode) return;
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prevHtmlBg = html.style.background;
    const prevBodyBg = body.style.background;
    const prevRootBg = root?.style?.background;
    html.style.background = 'transparent';
    body.style.background = 'transparent';
    if (root) root.style.background = 'transparent';
    return () => {
      html.style.background = prevHtmlBg;
      body.style.background = prevBodyBg;
      if (root) root.style.background = prevRootBg || '';
    };
  }, [buttonsOnlyMode]);

  // Update variable value handler
  const updateVariableValue = (name, value) => {
    if (!name) return;
    setDynamicVariableValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Update variable label handler (for display purposes only)
  const updateVariableLabel = (name, label) => {
    setDynamicVariableLabels(prev => ({
      ...prev,
      [name]: label
    }));
  };

  // Get display label for variable
  const getVariableLabel = (varName) => {
    if (!varName) return '';
    const customLabel = dynamicVariableLabels[varName];
    if (customLabel && customLabel.trim()) {
      return customLabel;
    }
    // Convert variable name to readable label (First_Name -> First Name)
    return varName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Load agent details
  useEffect(() => {
    const loadAgent = async () => {
      try {
        const response = await api.get(`/agents/${agentId}`);
        setAgent(response.data);
      } catch (err) {
        console.error('❌ Failed to load agent:', err);
        setError('Agent not found');
      } finally {
        setLoading(false);
      }
    };

    if (agentId) {
      loadAgent();
    } else {
      console.error('❌ No agent ID provided');
      setLoading(false);
    }
  }, [agentId]);

  // Start test session
  const startTestSession = async () => {
    if (!agent) return;

    try {
      setIsConnecting(true);
      setError(null);
      
      // Generate unique room name for test
      const timestamp = Date.now();
      const roomName = `test-agent-${agent.id}-${timestamp}`;
      
      
      // Build variables object from user-filled values
      const variablesToSend = {};
      dynamicVariableNames.forEach(name => {
        const value = dynamicVariableValues[name];
        if (value && value.trim()) {
          variablesToSend[name] = value.trim();
        }
      });

      const sessionPayload = {
        room_name: roomName,
        agent_id: agent.id,
        channel: 'test',
        // Include dynamic variables if user filled them in
        ...(Object.keys(variablesToSend).length > 0 && { variables: variablesToSend })
      };

      // Log variables for debugging
      if (Object.keys(variablesToSend).length > 0) {
        console.log('📊 Sending dynamic variables to session:', variablesToSend);
      }

      // Always use embed endpoint for public embed pages (no auth required)
      const sessionResponse = await api.post(
        '/sessions/embed/start',
        sessionPayload,
        embedToken
          ? { headers: { 'X-Embed-Token': embedToken } }
          : undefined
      );
      
      const testSessionId = sessionResponse.data.session_id;
      setSessionId(testSessionId);
      
      
      // Get LiveKit token
      const tokenResponse = await api.post('/token', {
        room: roomName,
        identity: 'embed-user'
      });
      
      const { url, token } = tokenResponse.data;
      
      // Create and connect to LiveKit room
      const newRoom = new Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: {
          resolution: { width: 1280, height: 720 }
        }
      });
      
      // Set up event listeners
      newRoom.on(RoomEvent.Connected, () => {
        setIsConnected(true);
        setIsConnecting(false);
        addCaption('system', '✅ Connected to agent');
      });
      
      newRoom.on(RoomEvent.Disconnected, () => {
        setIsConnected(false);
        addCaption('system', '❌ Disconnected from agent');
      });
      
      newRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        if (track.kind === 'audio') {
          // Play remote audio
          if (audioRef.current) {
            track.attach(audioRef.current);
          }
        }
      });
      
      newRoom.on(RoomEvent.DataReceived, (payload, participant) => {
        try {
          const decoder = new TextDecoder();
          const data = JSON.parse(decoder.decode(payload));
          
          // Handle transcription data
          if (data.type === 'transcription') {
            addCaption(data.speaker || 'agent', data.text);
          }
          
          // Handle conversation items
          if (data.type === 'conversation_item') {
            addCaption(data.speaker || 'agent', data.text);
          }
        } catch (e) {
          console.error('Error parsing data packet:', e);
        }
      });
      
      // Connect to room
      await newRoom.connect(url, token);
      setRoom(newRoom);
      
      // Enable microphone
      await newRoom.localParticipant.setMicrophoneEnabled(true);
      
    } catch (err) {
      console.error('❌ Error starting embed session:', err);
      const detail = err.response?.data?.detail || err.message || 'Failed to start call';
      setError(detail);
      setIsConnecting(false);
    }
  };

  // Stop test session
  const stopTestSession = async () => {
    try {
      if (room) {
        await room.disconnect();
        setRoom(null);
      }
      
      if (sessionId) {
        // End session in backend
        await api.post(`/sessions/${sessionId}/end`);
      }
      
      setIsConnected(false);
      setSessionId(null);
      setCaptions([]);
      
    } catch (err) {
      console.error('❌ Error stopping embed session:', err);
    }
  };

  // Add caption to transcript
  const addCaption = (speaker, text) => {
    if (!text || !text.trim()) return;
    
    setCaptions(prev => {
      // Deduplicate
      const lastCaption = prev[prev.length - 1];
      if (lastCaption && lastCaption.speaker === speaker && lastCaption.text === text) {
        return prev;
      }
      
      return [...prev, {
        speaker,
        text,
        timestamp: new Date().toLocaleTimeString()
      }];
    });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (room) {
        room.disconnect();
      }
    };
  }, [room]);

  if (loading) {
    if (minimalMode) {
      return (
        <div className="min-h-[180px] w-full flex items-center justify-center bg-transparent">
          <div className="text-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">Loading agent...</p>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading agent...</p>
        </div>
      </div>
    );
  }

  if (error && !agent) {
    if (minimalMode) {
      return (
        <div className="min-h-[180px] w-full flex items-center justify-center bg-transparent">
          <div className="text-center px-4">
            <p className="text-destructive text-sm mb-2">{error}</p>
            <p className="text-xs text-muted-foreground">Please check the agent embed URL.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive text-lg mb-4">{error}</p>
          <p className="text-muted-foreground">Please check the agent ID and try again.</p>
        </div>
      </div>
    );
  }

  const minimalOrDynamicMode = minimalMode || viewMode === 'dynamic';

  if (minimalOrDynamicMode) {
    // For buttons-only mode (view=buttons), show only icon buttons with no text
    if (minimalMode && viewMode !== 'dynamic') {
      return (
        <div className="min-h-[100px] w-full flex items-center justify-center bg-transparent">
          <div className="flex items-center justify-center">
            {!isConnected ? (
              <button
                onClick={startTestSession}
                disabled={isConnecting}
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-lg hover:scale-110 ${
                  isConnecting
                    ? 'bg-muted cursor-not-allowed text-muted-foreground'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
                aria-label="Start call"
              >
                {isConnecting ? (
                  <Loader2 className="w-7 h-7 animate-spin" />
                ) : (
                  <Phone className="w-7 h-7" />
                )}
              </button>
            ) : (
              <button
                onClick={stopTestSession}
                className="w-14 h-14 rounded-full flex items-center justify-center transition-all bg-red-500 hover:bg-red-600 text-white shadow-lg hover:scale-110"
                aria-label="End call"
              >
                <PhoneOff className="w-7 h-7" />
              </button>
            )}
          </div>
          {/* Hidden audio element for agent voice */}
          <audio ref={audioRef} autoPlay playsInline />
        </div>
      );
    }

    // Dynamic mode with variables - keep the form UI
    return (
      <div className="min-h-[200px] w-full flex items-center justify-center bg-transparent">
        <div className={`w-full ${viewMode === 'dynamic' && dynamicVariableNames.length > 0 ? 'max-w-md' : 'max-w-xs'} bg-card/80 border border-border rounded-xl shadow-lg p-5 flex flex-col gap-4`}>
          <div className="text-center">
            <h2 className="text-base font-semibold text-foreground">{agent?.name || 'Voice Agent'}</h2>
            <p className="text-xs text-muted-foreground mt-1">
              {viewMode === 'dynamic' && dynamicVariableNames.length > 0 
                ? 'Fill in your information below, then start the call.'
                : 'Click start to connect, stop when you\'re done.'}
            </p>
          </div>

          {/* Dynamic Variable Input Fields */}
          {viewMode === 'dynamic' && (
            <div className="w-full space-y-3">
              {dynamicVariableNames.length === 0 && !isConnected && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  No variables configured for this embed.
                </p>
              )}
              {dynamicVariableNames.map((varName) => {
                const label = getVariableLabel(varName);
                return (
                  <div key={varName} className="space-y-1">
                    <label className="text-xs font-medium text-foreground block">
                      {label}
                    </label>
                    <input
                      type="text"
                      value={dynamicVariableValues[varName] || ''}
                      onChange={(e) => updateVariableValue(varName, e.target.value)}
                      placeholder={`Enter ${label.toLowerCase()}`}
                      disabled={isConnected}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                );
              })}
            </div>
          )}

          {error && (
            <div className="w-full text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <div className="w-full flex justify-center">
            {!isConnected ? (
              <button
                onClick={startTestSession}
                disabled={isConnecting}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-md ${
                  isConnecting
                    ? 'bg-muted cursor-not-allowed text-muted-foreground'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
                aria-label="Start call"
              >
                {isConnecting ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <Phone className="w-6 h-6" />
                )}
              </button>
            ) : (
              <button
                onClick={stopTestSession}
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all bg-red-500 hover:bg-red-600 text-white shadow-md"
                aria-label="End call"
              >
                <PhoneOff className="w-6 h-6" />
              </button>
            )}
          </div>

          <div className="w-full text-center">
            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-medium ${
              isConnected
                ? 'bg-success/10 text-success border border-success/30'
                : isConnecting
                ? 'bg-warning/10 text-warning border border-warning/30'
                : 'bg-muted/50 text-muted-foreground border border-border/60'
            }`}>
              <span className={`w-2 h-2 rounded-full ${
                isConnected ? 'bg-success animate-pulse' :
                isConnecting ? 'bg-warning animate-pulse' :
                'bg-muted-foreground'
              }`}></span>
              {isConnected ? 'Live' : isConnecting ? 'Connecting…' : 'Ready'}
            </span>
          </div>
        </div>

        {/* Hidden audio element for agent voice */}
        <audio ref={audioRef} autoPlay playsInline />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-900 flex flex-col">
      {/* Header */}
      <div className="bg-card/80 backdrop-blur-sm border-b border-border p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-foreground">{agent?.name || 'Voice Agent'}</h1>
            <p className="text-sm text-muted-foreground">Click the button below to start a conversation</p>
          </div>
          {isConnected && (
            <div className="flex items-center gap-2 text-success">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">Connected</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 max-w-4xl mx-auto w-full">
        {/* Dynamic Variable Input Fields (Full Mode) */}
        {viewMode === 'dynamic' && (
          <div className="w-full max-w-md mb-8 space-y-4">
            <div className="bg-card/50 border border-border rounded-lg p-6">
              <h3 className="text-sm font-semibold text-foreground mb-4">Your Information</h3>
              <div className="space-y-4">
                {dynamicVariableNames.length === 0 && !isConnected && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No variables configured for this embed.
                  </p>
                )}
                {dynamicVariableNames.map((varName) => {
                  const label = getVariableLabel(varName);
                  return (
                    <div key={varName} className="space-y-2">
                      <label className="text-sm font-medium text-foreground block">
                        {label}
                      </label>
                      <input
                        type="text"
                        value={dynamicVariableValues[varName] || ''}
                        onChange={(e) => updateVariableValue(varName, e.target.value)}
                        placeholder={`Enter ${label.toLowerCase()}`}
                        disabled={isConnected}
                        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Call Button */}
        <div className="mb-8">
          {!isConnected ? (
            <button
              onClick={startTestSession}
              disabled={isConnecting}
              className={`w-16 h-16 rounded-full flex items-center justify-center transition-all shadow-lg hover:scale-105 ${
                isConnecting
                  ? 'bg-muted cursor-not-allowed text-muted-foreground'
                  : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
              aria-label="Start call"
            >
              {isConnecting ? (
                <Loader2 className="w-8 h-8 animate-spin" />
              ) : (
                <Phone className="w-8 h-8" />
              )}
            </button>
          ) : (
            <button
              onClick={stopTestSession}
              className="w-16 h-16 rounded-full flex items-center justify-center transition-all bg-red-500 hover:bg-red-600 text-white shadow-lg hover:scale-105"
              aria-label="End call"
            >
              <PhoneOff className="w-8 h-8" />
            </button>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/30 text-destructive rounded-lg max-w-md">
            {error}
          </div>
        )}

        {/* Transcript */}
        {captions.length > 0 && (
          <div className="w-full bg-card/80 backdrop-blur-sm border border-border rounded-lg p-6 max-h-96 overflow-y-auto">
            <h3 className="text-sm font-semibold text-foreground mb-4">Conversation</h3>
            <div className="space-y-3">
              {captions.map((caption, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg ${
                    caption.speaker === 'user'
                      ? 'bg-primary/10 border border-primary/30'
                      : caption.speaker === 'ai' || caption.speaker === 'agent'
                      ? 'bg-secondary-cyan/10 border border-secondary-cyan/30'
                      : 'bg-muted/20 border border-border'
                  }`}
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium ${
                          caption.speaker === 'user'
                            ? 'text-primary'
                            : caption.speaker === 'ai' || caption.speaker === 'agent'
                            ? 'text-secondary-cyan'
                            : 'text-muted-foreground'
                        }`}>
                          {caption.speaker === 'user' ? 'You' : 
                           caption.speaker === 'ai' || caption.speaker === 'agent' ? 'Agent' : 
                           'System'}
                        </span>
                      </div>
                      <p className="text-sm text-foreground">{caption.text}</p>
                    </div>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {caption.timestamp}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        {!isConnected && captions.length === 0 && (
          <div className="mt-8 text-center text-muted-foreground max-w-md">
            <p className="text-sm">
              {viewMode === 'dynamic' && dynamicVariableNames.length > 0
                ? 'Fill in your information above, then click the button to start a personalized conversation.'
                : `Click the button above to start a voice conversation with ${agent?.name || 'the agent'}.`}
              {' '}Make sure your microphone is enabled.
            </p>
          </div>
        )}
      </div>

      {/* Hidden audio element for agent voice */}
      <audio ref={audioRef} autoPlay playsInline />
    </div>
  );
};

const EmbedAgentPage = () => {
  const { agentId } = useParams();
  return <EmbedAgentPageContent agentId={agentId} />;
};

export { EmbedAgentPageContent };
export default EmbedAgentPage;

